import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share, PlusSquare, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed/standalone
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    
    if (standalone) {
      return; // Already installed, don't show prompt
    }

    // Check if dismissed recently (within 3 days - shorter for better UX)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 3) {
        return;
      }
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    if (isIOSDevice) {
      // Show iOS instructions after login (delay to not overwhelm)
      const timer = setTimeout(() => setShowPrompt(true), 2000);
      return () => clearTimeout(timer);
    }

    // Android/Desktop: Listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 1500);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installée avec succès');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
    setShowIOSInstructions(false);
  };

  // Don't show if already in standalone mode
  if (isStandalone) return null;
  if (!showPrompt) return null;

  // iOS Full-Screen Instructions Modal
  if (isIOS && showIOSInstructions) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center">
        <div className="bg-slate-900 rounded-t-3xl sm:rounded-2xl p-6 w-full sm:max-w-md shadow-2xl border-t border-slate-700 sm:border animate-in slide-in-from-bottom duration-300">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                <Smartphone className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Installer l'app</h3>
                <p className="text-sm text-slate-400">3 étapes simples</p>
              </div>
            </div>
            <button onClick={handleDismiss} className="text-slate-400 hover:text-white p-2">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Steps */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Appuyez sur Partager</p>
                <p className="text-sm text-slate-400">Icône en bas de Safari</p>
              </div>
              <Share className="w-6 h-6 text-violet-400" />
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Sur l'écran d'accueil</p>
                <p className="text-sm text-slate-400">Faites défiler et appuyez</p>
              </div>
              <PlusSquare className="w-6 h-6 text-violet-400" />
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/50 border border-slate-700">
              <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                3
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Ajouter</p>
                <p className="text-sm text-slate-400">En haut à droite</p>
              </div>
              <ChevronRight className="w-6 h-6 text-violet-400" />
            </div>
          </div>
          
          <Button 
            onClick={handleDismiss}
            className="w-full bg-violet-600 hover:bg-violet-700 h-12 text-base font-medium"
          >
            J'ai compris
          </Button>
        </div>
      </div>
    );
  }

  // Compact Install Banner
  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[9998] animate-in slide-in-from-bottom duration-500">
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-4 shadow-2xl border border-slate-700">
        <div className="flex items-start gap-3">
          {/* App Icon */}
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg flex-shrink-0">
            <img 
              src="/bbm-logo.png" 
              alt="BBM" 
              className="w-8 h-8 rounded"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-white mb-0.5">BBM</h3>
            <p className="text-xs text-slate-400 mb-3">
              {isIOS 
                ? 'Ajoutez l\'app à votre écran d\'accueil'
                : 'Installez l\'app pour un accès rapide'
              }
            </p>
            <div className="flex gap-2">
              {isIOS ? (
                <Button 
                  size="sm"
                  onClick={() => setShowIOSInstructions(true)}
                  className="bg-violet-600 hover:bg-violet-700 text-xs h-8 px-4"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Installer
                </Button>
              ) : (
                <Button 
                  size="sm"
                  onClick={handleInstall}
                  className="bg-violet-600 hover:bg-violet-700 text-xs h-8 px-4"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Installer
                </Button>
              )}
              <Button 
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="text-slate-400 hover:text-white text-xs h-8"
              >
                Plus tard
              </Button>
            </div>
          </div>
          
          <button onClick={handleDismiss} className="text-slate-500 hover:text-white -mt-1">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
